import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Building2, Search, X } from 'lucide-react';
import { companyService } from '../services';
import { messageHandler } from '../services/message-handler.service';
import type { Company } from '../types/company';
import { unformatDocument } from '../types/api';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';
import CompanyList from '../components/companies/CompanyList';
import CompanyForm from '../components/companies/CompanyForm';
import Modal from '../components/ui/Modal';
import Pagination from '../components/ui/Pagination';
import { FormField, FormLabel, FormInput, FormSelect } from '../components/ui';
import { FormCpfCnpj, FormPhone } from '../components/ui/FormControls';

type ModalType = 'create' | 'edit' | '';

interface ModalState {
  type: ModalType;
  title?: string;
}

interface CompanyFilters {
  company_name?: string;
  cnpj?: string;
  phone?: string;
  email?: string;
  city?: string;
  page?: number;
  limit?: number;
}

const DEFAULT_LIMIT = 20;

function Companies() {
  const [modal, setModal] = useState<ModalState>({ type: '' });
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [filters, setFilters] = useState<CompanyFilters>({
    page: 1,
    limit: DEFAULT_LIMIT,
  });
  const [localFilters, setLocalFilters] = useState<CompanyFilters>({
    page: 1,
    limit: DEFAULT_LIMIT,
  });
  const queryClient = useQueryClient();

  // Queries
  const { data: companyResponse, isLoading } = useQuery({
    queryKey: ['companies', filters],
    queryFn: () => companyService.getCompanies(filters),
    keepPreviousData: true,
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: Partial<Company>) => companyService.createCompany(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      messageHandler.success('Empresa criada com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Company) => companyService.updateCompany(data.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      messageHandler.success('Empresa atualizada com sucesso!');
      closeModal();
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => companyService.deleteCompany(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      messageHandler.success('Empresa excluída com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  // Handlers
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);
    const data = {
      cnpj: unformatDocument(formData.get('cnpj') as string),
      company_name: formData.get('company_name') as string,
      address: formData.get('address') as string,
      city: formData.get('city') as string,
      phone: unformatDocument(formData.get('phone') as string),
      email: formData.get('email') as string,
    };

    if (editingCompany) {
      updateMutation.mutate({ ...editingCompany, ...data });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (company: Company) => {
    messageHandler.confirm('Excluir Empresa', 'Tem certeza que deseja excluir esta empresa?', () =>
      deleteMutation.mutate(company.id)
    );
  };

  const handleLocalFilterChange = (name: string, value: string) => {
    setLocalFilters(prev => ({
      ...prev,
      [name]: value || undefined,
    }));
  };

  const handleResetFilters = () => {
    setLocalFilters({
      page: 1,
      limit: DEFAULT_LIMIT,
    });
    setFilters({
      page: 1,
      limit: DEFAULT_LIMIT,
    });
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({
      ...prev,
      page,
    }));
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleApplyFilters = () => {
    setFilters({
      ...localFilters,
      page: 1, // Reset to first page when applying new filters
    });
  };

  const closeModal = () => {
    setModal({ type: '' });
    setEditingCompany(null);
  };

  const companies = companyResponse?.data || [];
  const pagination = companyResponse?.pagination;
  const totalRecords = pagination?.total || 0;
  const isFiltered = Object.keys(filters).some(
    key => key !== 'page' && key !== 'limit' && filters[key as keyof CompanyFilters]
  );

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Empresas"
          description="Gerencie as empresas participantes."
          actions={
            <Button
              onClick={() => {
                setEditingCompany(null);
                setModal({ type: 'create' });
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Nova Empresa
            </Button>
          }
        />

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow mb-6">
          <form className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              <FormField>
                <FormLabel htmlFor="company_name">Nome da Empresa</FormLabel>
                <FormInput
                  id="company_name"
                  name="company_name"
                  value={localFilters.company_name || ''}
                  onChange={e => handleLocalFilterChange('company_name', e.target.value)}
                  placeholder="Buscar por nome..."
                />
              </FormField>

              <FormField>
                <FormLabel htmlFor="cnpj">CNPJ</FormLabel>
                <FormCpfCnpj
                  id="cnpj"
                  name="cnpj"
                  type="cnpj"
                  value={localFilters.cnpj || ''}
                  onChange={e => handleLocalFilterChange('cnpj', e.target.value)}
                  placeholder="Buscar por CNPJ"
                />
              </FormField>

              <FormField>
                <FormLabel htmlFor="city">Cidade</FormLabel>
                <FormInput
                  id="city"
                  name="city"
                  value={localFilters.city || ''}
                  onChange={e => handleLocalFilterChange('city', e.target.value)}
                  placeholder="Buscar por cidade..."
                />
              </FormField>

              <FormField>
                <FormLabel htmlFor="phone">Telefone</FormLabel>
                <FormPhone
                  id="phone"
                  name="phone"
                  value={localFilters.phone || ''}
                  onChange={e => handleLocalFilterChange('phone', e.target.value)}
                  placeholder="Buscar por telefone..."
                />
              </FormField>

              <FormField>
                <FormLabel htmlFor="email">Email</FormLabel>
                <FormInput
                  id="email"
                  name="email"
                  value={localFilters.email || ''}
                  onChange={e => handleLocalFilterChange('email', e.target.value)}
                  placeholder="Buscar por email..."
                />
              </FormField>
            </div>

            <div className="flex justify-end mt-4 space-x-2">
              {isFiltered && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleResetFilters}
                  className="flex items-center"
                >
                  <X className="h-4 w-4 mr-2" />
                  Limpar Filtros
                </Button>
              )}
              <Button type="button" onClick={handleApplyFilters} className="flex items-center">
                <Search className="h-4 w-4 mr-2" />
                Filtrar
              </Button>
            </div>
          </form>
        </div>

        {/* Total Records Count */}
        <div className="mb-4 flex items-center text-sm text-gray-600 dark:text-gray-400">
          <Building2 className="h-4 w-4 mr-2" />
          <span>
            Total de registros: <strong>{totalRecords}</strong>
          </span>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : companies.length > 0 ? (
          <>
            <CompanyList
              companies={companies}
              onEdit={company => {
                setEditingCompany(company);
                setModal({ type: 'edit' });
              }}
              onDelete={handleDelete}
            />

            {pagination && pagination.last_page > 1 && (
              <div className="mt-6">
                <Pagination
                  currentPage={pagination.current_page}
                  totalPages={pagination.last_page}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        ) : (
          <EmptyState
            icon={<Building2 className="h-12 w-12" />}
            title={isFiltered ? 'Nenhuma empresa encontrada' : 'Nenhuma empresa cadastrada'}
            description={
              isFiltered
                ? 'Tente ajustar os filtros para encontrar o que procura'
                : 'Cadastre sua primeira empresa para começar'
            }
            action={{
              label: 'Criar Empresa',
              onClick: () => {
                setEditingCompany(null);
                setModal({ type: 'create' });
              },
            }}
          />
        )}

        <Modal
          isOpen={modal.type === 'create' || modal.type === 'edit'}
          onClose={closeModal}
          title={editingCompany ? 'Editar Empresa' : 'Nova Empresa'}
        >
          <CompanyForm
            company={editingCompany}
            onSubmit={handleSubmit}
            isLoading={createMutation.isPending || updateMutation.isPending}
            onClose={closeModal}
            validationErrors={{}}
          />
        </Modal>
      </div>
    </div>
  );
}

export default Companies;
